import React, { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../services/authService'
import {
  SparklesIcon,
  BookOpenIcon,
  ClockIcon,
  BellAlertIcon,
  ChartBarIcon,
  PlayIcon,
  StopIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  DocumentTextIcon,
  CogIcon,
  EyeIcon,
  ChevronRightIcon,
  ArrowTrendingUpIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  BoltIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

// ============================================================
// CONSTANTS
// ============================================================

const TABS = [
  { key: 'estrategista', label: 'IA Estrategista', icon: SparklesIcon },
  { key: 'knowledge', label: 'Knowledge Base', icon: BookOpenIcon },
  { key: 'scheduler', label: 'Scheduler', icon: ClockIcon },
  { key: 'alertas', label: 'Alertas', icon: BellAlertIcon },
  { key: 'metricas', label: 'Metricas', icon: ChartBarIcon },
]

const PERIODOS = [
  { label: '7d', value: 7 },
  { label: '15d', value: 15 },
  { label: '30d', value: 30 },
  { label: '60d', value: 60 },
  { label: '90d', value: 90 },
]

const ANALISE_TIPOS = [
  { value: 'completa', label: 'Completa' },
  { value: 'leads', label: 'Leads' },
  { value: 'campanhas', label: 'Campanhas' },
  { value: 'conversao', label: 'Conversao' },
  { value: 'financeiro', label: 'Financeiro' },
]

const KB_CATEGORIAS = [
  { value: 'faq', label: 'FAQ', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'politicas', label: 'Politicas', color: 'bg-purple-500/20 text-purple-400' },
  { value: 'contratos', label: 'Contratos', color: 'bg-emerald-500/20 text-emerald-400' },
  { value: 'veiculos', label: 'Veiculos', color: 'bg-amber-500/20 text-amber-400' },
  { value: 'financeiro', label: 'Financeiro', color: 'bg-pink-500/20 text-pink-400' },
  { value: 'geral', label: 'Geral', color: 'bg-gray-500/20 text-gray-400' },
]

const METRIC_PERIODS = [
  { label: '7d', value: 7 },
  { label: '15d', value: 15 },
  { label: '30d', value: 30 },
]

// ============================================================
// HELPER FUNCTIONS
// ============================================================

function getScoreColor(score) {
  const s = Number(score) || 0
  if (s >= 80) return 'text-green-400'
  if (s >= 60) return 'text-blue-400'
  if (s >= 40) return 'text-yellow-400'
  return 'text-red-400'
}

function getScoreRingColor(score) {
  const s = Number(score) || 0
  if (s >= 80) return '#4ade80'
  if (s >= 60) return '#60a5fa'
  if (s >= 40) return '#facc15'
  return '#f87171'
}

function getScoreLabel(score) {
  const s = Number(score) || 0
  if (s >= 80) return 'Excelente'
  if (s >= 60) return 'Bom'
  if (s >= 40) return 'Regular'
  return 'Critico'
}

function formatDate(dateStr) {
  if (!dateStr) return '---'
  try {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function formatDateShort(dateStr) {
  if (!dateStr) return '---'
  try {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    })
  } catch {
    return dateStr
  }
}

// ============================================================
// CIRCULAR SCORE GAUGE
// ============================================================

function ScoreGauge({ score, size = 160 }) {
  const s = Number(score) || 0
  const radius = (size - 20) / 2
  const circumference = 2 * Math.PI * radius
  const progress = (s / 100) * circumference
  const color = getScoreRingColor(s)

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="10"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold ${getScoreColor(s)}`}>{s}</span>
        <span className="text-xs text-gray-400 mt-1">{getScoreLabel(s)}</span>
      </div>
    </div>
  )
}

// ============================================================
// MAIN COMPONENT
// ============================================================

const MarketingAIPage = () => {
  const [activeTab, setActiveTab] = useState('estrategista')
  const [loading, setLoading] = useState(false)

  // IA Estrategista state
  const [iaConfig, setIaConfig] = useState(null)
  const [selectedPeriodo, setSelectedPeriodo] = useState(30)
  const [selectedTipo, setSelectedTipo] = useState('completa')
  const [analyzing, setAnalyzing] = useState(false)
  const [latestReport, setLatestReport] = useState(null)
  const [reports, setReports] = useState([])
  const [reportsPage, setReportsPage] = useState(1)
  const [reportsPagination, setReportsPagination] = useState({ total: 0, pages: 1 })
  const [selectedReport, setSelectedReport] = useState(null)
  const [executingAction, setExecutingAction] = useState(null)

  // Knowledge Base state
  const [kbArticles, setKbArticles] = useState([])
  const [kbSearch, setKbSearch] = useState('')
  const [kbFilterCategoria, setKbFilterCategoria] = useState('')
  const [showKbModal, setShowKbModal] = useState(false)
  const [editingKb, setEditingKb] = useState(null)
  const [kbForm, setKbForm] = useState({ titulo: '', conteudo: '', categoria: 'geral', tags: '' })

  // Scheduler state
  const [schedulerStatus, setSchedulerStatus] = useState(null)
  const [schedulerLogs, setSchedulerLogs] = useState([])
  const [logFilterJob, setLogFilterJob] = useState('')
  const [logFilterStatus, setLogFilterStatus] = useState('')
  const schedulerInterval = useRef(null)

  // Alertas state
  const [alertas, setAlertas] = useState([])
  const [showAlertaModal, setShowAlertaModal] = useState(false)
  const [editingAlerta, setEditingAlerta] = useState(null)
  const [alertaForm, setAlertaForm] = useState({
    nome: '',
    tipo: 'lead_score',
    condicao: '',
    canal_notificacao: 'email',
    destinatarios: '',
    ativo: true,
  })

  // Metricas state
  const [metricasPeriodo, setMetricasPeriodo] = useState(15)
  const [metricasDiarias, setMetricasDiarias] = useState([])

  // ============================================================
  // DATA FETCHING
  // ============================================================

  const loadIaConfig = useCallback(async () => {
    try {
      const res = await api.get('/api/marketing/ia/config')
      setIaConfig(res.data)
    } catch (err) {
      console.error('Erro ao carregar config IA:', err)
    }
  }, [])

  const loadReports = useCallback(async () => {
    try {
      setLoading(true)
      const res = await api.get('/api/marketing/ia/reports', {
        params: { page: reportsPage, limit: 10 },
      })
      const data = res.data
      setReports(data.data || data.reports || data || [])
      if (data.pagination) {
        setReportsPagination(data.pagination)
      }
      // Set latest report
      const list = data.data || data.reports || data || []
      if (list.length > 0 && !latestReport) {
        setLatestReport(list[0])
      }
    } catch (err) {
      console.error('Erro ao carregar relatorios:', err)
    } finally {
      setLoading(false)
    }
  }, [reportsPage])

  const loadKnowledgeBase = useCallback(async () => {
    try {
      setLoading(true)
      const params = {}
      if (kbSearch) params.search = kbSearch
      if (kbFilterCategoria) params.categoria = kbFilterCategoria
      const res = await api.get('/api/marketing/ia/knowledge-base', { params })
      setKbArticles(res.data?.data || res.data || [])
    } catch (err) {
      console.error('Erro ao carregar knowledge base:', err)
    } finally {
      setLoading(false)
    }
  }, [kbSearch, kbFilterCategoria])

  const loadSchedulerStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/marketing/ia/scheduler/status')
      setSchedulerStatus(res.data)
    } catch (err) {
      console.error('Erro ao carregar scheduler:', err)
    }
  }, [])

  const loadSchedulerLogs = useCallback(async () => {
    try {
      const params = { limit: 50 }
      if (logFilterJob) params.job_name = logFilterJob
      if (logFilterStatus) params.status = logFilterStatus
      const res = await api.get('/api/marketing/ia/scheduler/logs', { params })
      setSchedulerLogs(res.data?.data || res.data || [])
    } catch (err) {
      console.error('Erro ao carregar logs:', err)
    }
  }, [logFilterJob, logFilterStatus])

  const loadAlertas = useCallback(async () => {
    try {
      setLoading(true)
      const res = await api.get('/api/marketing/ia/alertas')
      setAlertas(res.data?.data || res.data || [])
    } catch (err) {
      console.error('Erro ao carregar alertas:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadMetricasDiarias = useCallback(async () => {
    try {
      setLoading(true)
      const res = await api.get('/api/marketing/ia/metricas-diarias', {
        params: { periodo: metricasPeriodo },
      })
      setMetricasDiarias(res.data?.data || res.data || [])
    } catch (err) {
      console.error('Erro ao carregar metricas:', err)
    } finally {
      setLoading(false)
    }
  }, [metricasPeriodo])

  // ============================================================
  // EFFECTS
  // ============================================================

  useEffect(() => {
    if (activeTab === 'estrategista') {
      loadIaConfig()
      loadReports()
    } else if (activeTab === 'knowledge') {
      loadKnowledgeBase()
    } else if (activeTab === 'scheduler') {
      loadSchedulerStatus()
      loadSchedulerLogs()
    } else if (activeTab === 'alertas') {
      loadAlertas()
    } else if (activeTab === 'metricas') {
      loadMetricasDiarias()
    }
  }, [activeTab])

  useEffect(() => {
    if (activeTab === 'estrategista') {
      loadReports()
    }
  }, [reportsPage])

  useEffect(() => {
    if (activeTab === 'knowledge') {
      loadKnowledgeBase()
    }
  }, [kbSearch, kbFilterCategoria])

  useEffect(() => {
    if (activeTab === 'scheduler') {
      loadSchedulerLogs()
    }
  }, [logFilterJob, logFilterStatus])

  useEffect(() => {
    if (activeTab === 'metricas') {
      loadMetricasDiarias()
    }
  }, [metricasPeriodo])

  // Auto-refresh scheduler every 30s
  useEffect(() => {
    if (activeTab === 'scheduler') {
      schedulerInterval.current = setInterval(() => {
        loadSchedulerStatus()
        loadSchedulerLogs()
      }, 30000)
    }
    return () => {
      if (schedulerInterval.current) {
        clearInterval(schedulerInterval.current)
        schedulerInterval.current = null
      }
    }
  }, [activeTab])

  // ============================================================
  // ACTIONS - IA Estrategista
  // ============================================================

  const handleGenerateAnalysis = async () => {
    setAnalyzing(true)
    try {
      const res = await api.post('/api/marketing/ia/analyze', {
        periodo_dias: selectedPeriodo,
        tipo: selectedTipo,
      })
      toast.success('Analise gerada com sucesso!')
      const report = res.data?.report || res.data
      setLatestReport(report)
      loadReports()
    } catch (err) {
      console.error('Erro ao gerar analise:', err)
      toast.error(err.response?.data?.error || 'Erro ao gerar analise')
    } finally {
      setAnalyzing(false)
    }
  }

  const handleViewReport = async (reportId) => {
    try {
      const res = await api.get(`/api/marketing/ia/reports/${reportId}`)
      setSelectedReport(res.data)
    } catch (err) {
      toast.error('Erro ao carregar relatorio')
    }
  }

  const handleExecuteAction = async (reportId, actionIndex) => {
    setExecutingAction(actionIndex)
    try {
      await api.post(`/api/marketing/ia/reports/${reportId}/execute-action`, {
        action_index: actionIndex,
      })
      toast.success('Acao executada com sucesso!')
      if (selectedReport) {
        handleViewReport(reportId)
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao executar acao')
    } finally {
      setExecutingAction(null)
    }
  }

  // ============================================================
  // ACTIONS - Knowledge Base
  // ============================================================

  const handleSaveKb = async () => {
    if (!kbForm.titulo.trim() || !kbForm.conteudo.trim()) {
      toast.error('Titulo e conteudo sao obrigatorios')
      return
    }
    try {
      const payload = {
        ...kbForm,
        tags: kbForm.tags
          ? kbForm.tags.split(',').map((t) => t.trim()).filter(Boolean)
          : [],
      }
      if (editingKb) {
        await api.put(`/api/marketing/ia/knowledge-base/${editingKb.id}`, payload)
        toast.success('Artigo atualizado!')
      } else {
        await api.post('/api/marketing/ia/knowledge-base', payload)
        toast.success('Artigo criado!')
      }
      setShowKbModal(false)
      setEditingKb(null)
      setKbForm({ titulo: '', conteudo: '', categoria: 'geral', tags: '' })
      loadKnowledgeBase()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar artigo')
    }
  }

  const handleDeleteKb = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este artigo?')) return
    try {
      await api.delete(`/api/marketing/ia/knowledge-base/${id}`)
      toast.success('Artigo excluido!')
      loadKnowledgeBase()
    } catch (err) {
      toast.error('Erro ao excluir artigo')
    }
  }

  const openEditKb = (article) => {
    setEditingKb(article)
    setKbForm({
      titulo: article.titulo || '',
      conteudo: article.conteudo || '',
      categoria: article.categoria || 'geral',
      tags: Array.isArray(article.tags) ? article.tags.join(', ') : article.tags || '',
    })
    setShowKbModal(true)
  }

  // ============================================================
  // ACTIONS - Scheduler
  // ============================================================

  const handleStartScheduler = async () => {
    try {
      await api.post('/api/marketing/ia/scheduler/start')
      toast.success('Scheduler iniciado!')
      loadSchedulerStatus()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao iniciar scheduler')
    }
  }

  const handleStopScheduler = async () => {
    try {
      await api.post('/api/marketing/ia/scheduler/stop')
      toast.success('Scheduler parado!')
      loadSchedulerStatus()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao parar scheduler')
    }
  }

  // ============================================================
  // ACTIONS - Alertas
  // ============================================================

  const handleSaveAlerta = async () => {
    if (!alertaForm.nome.trim()) {
      toast.error('Nome do alerta e obrigatorio')
      return
    }
    try {
      if (editingAlerta) {
        await api.put(`/api/marketing/ia/alertas/${editingAlerta.id}`, alertaForm)
        toast.success('Alerta atualizado!')
      } else {
        await api.post('/api/marketing/ia/alertas', alertaForm)
        toast.success('Alerta criado!')
      }
      setShowAlertaModal(false)
      setEditingAlerta(null)
      setAlertaForm({ nome: '', tipo: 'lead_score', condicao: '', canal_notificacao: 'email', destinatarios: '', ativo: true })
      loadAlertas()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar alerta')
    }
  }

  const handleToggleAlerta = async (alerta) => {
    try {
      await api.put(`/api/marketing/ia/alertas/${alerta.id}`, {
        ...alerta,
        ativo: !alerta.ativo,
      })
      toast.success(alerta.ativo ? 'Alerta desativado' : 'Alerta ativado')
      loadAlertas()
    } catch (err) {
      toast.error('Erro ao atualizar alerta')
    }
  }

  const handleDeleteAlerta = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este alerta?')) return
    try {
      await api.delete(`/api/marketing/ia/alertas/${id}`)
      toast.success('Alerta excluido!')
      loadAlertas()
    } catch (err) {
      toast.error('Erro ao excluir alerta')
    }
  }

  const openEditAlerta = (alerta) => {
    setEditingAlerta(alerta)
    setAlertaForm({
      nome: alerta.nome || '',
      tipo: alerta.tipo || 'lead_score',
      condicao: alerta.condicao || '',
      canal_notificacao: alerta.canal_notificacao || 'email',
      destinatarios: alerta.destinatarios || '',
      ativo: alerta.ativo !== false,
    })
    setShowAlertaModal(true)
  }

  // ============================================================
  // RENDER - Tab: IA Estrategista
  // ============================================================

  const renderEstrategista = () => {
    const report = selectedReport || latestReport

    return (
      <div className="space-y-6">
        {/* Analysis Controls */}
        <div className="bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-700">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <SparklesIcon className="w-5 h-5 text-purple-400" />
                Gerar Nova Analise
              </h3>
              <div className="flex flex-wrap gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Periodo</label>
                  <div className="flex gap-1">
                    {PERIODOS.map((p) => (
                      <button
                        key={p.value}
                        onClick={() => setSelectedPeriodo(p.value)}
                        className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-all ${
                          selectedPeriodo === p.value
                            ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Tipo</label>
                  <select
                    value={selectedTipo}
                    onChange={(e) => setSelectedTipo(e.target.value)}
                    className="bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {ANALISE_TIPOS.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <button
              onClick={handleGenerateAnalysis}
              disabled={analyzing}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg shadow-purple-600/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {analyzing ? (
                <>
                  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Analisando...
                </>
              ) : (
                <>
                  <RocketLaunchIcon className="w-5 h-5" />
                  Gerar Analise
                </>
              )}
            </button>
          </div>
        </div>

        {/* Latest Report */}
        {report && (
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 px-6 py-4 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <DocumentTextIcon className="w-5 h-5 text-purple-400" />
                  {selectedReport ? 'Relatorio Detalhado' : 'Ultimo Relatorio'}
                </h3>
                {selectedReport && (
                  <button
                    onClick={() => setSelectedReport(null)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-400 mt-1">
                {report.tipo && <span className="capitalize">{report.tipo}</span>}
                {report.periodo_dias && <span> - {report.periodo_dias} dias</span>}
                {report.created_at && <span> - {formatDate(report.created_at)}</span>}
              </p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Score Gauge */}
                <div className="flex flex-col items-center justify-center">
                  <ScoreGauge score={report.score_saude} />
                  <p className="text-sm text-gray-400 mt-3">Score de Saude</p>
                </div>

                {/* Analysis Text */}
                <div className="lg:col-span-2">
                  <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">
                    Analise
                  </h4>
                  <div className="bg-gray-900/50 rounded-lg p-4 text-gray-300 text-sm leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto scrollbar-thin">
                    {report.analise_texto || 'Sem analise disponivel.'}
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              {report.recomendacoes && report.recomendacoes.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <LightBulbIcon className="w-4 h-4 text-yellow-400" />
                    Recomendacoes
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {report.recomendacoes.map((rec, idx) => {
                      const recText = typeof rec === 'string' ? rec : rec.descricao || rec.texto || rec.title || JSON.stringify(rec)
                      const recPriority = typeof rec === 'object' ? rec.prioridade || rec.priority : null
                      return (
                        <div
                          key={idx}
                          className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 hover:border-purple-500/50 transition-colors group"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <ChevronRightIcon className="w-4 h-4 text-purple-400 flex-shrink-0" />
                                {recPriority && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    recPriority === 'alta' ? 'bg-red-500/20 text-red-400' :
                                    recPriority === 'media' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-green-500/20 text-green-400'
                                  }`}>
                                    {recPriority}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-300">{recText}</p>
                            </div>
                            <button
                              onClick={() => handleExecuteAction(report.id, idx)}
                              disabled={executingAction === idx}
                              className="flex-shrink-0 px-3 py-1.5 text-xs bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600 hover:text-white transition-all font-medium disabled:opacity-50"
                            >
                              {executingAction === idx ? (
                                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                              ) : (
                                'Executar'
                              )}
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Next Steps */}
              {report.proximos_passos && report.proximos_passos.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <ArrowTrendingUpIcon className="w-4 h-4 text-blue-400" />
                    Proximos Passos
                  </h4>
                  <div className="space-y-2">
                    {report.proximos_passos.map((step, idx) => {
                      const stepText = typeof step === 'string' ? step : step.descricao || step.texto || JSON.stringify(step)
                      return (
                        <div key={idx} className="flex items-start gap-3 bg-gray-900/50 rounded-lg p-3 border border-gray-700">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600/20 text-blue-400 text-xs font-bold flex items-center justify-center mt-0.5">
                            {idx + 1}
                          </span>
                          <p className="text-sm text-gray-300">{stepText}</p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reports History */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700">
          <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              Historico de Relatorios
            </h3>
            <button
              onClick={loadReports}
              className="text-gray-400 hover:text-white transition-colors"
              title="Atualizar"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-gray-700">
                  <th className="px-6 py-3">Data</th>
                  <th className="px-6 py-3">Tipo</th>
                  <th className="px-6 py-3">Periodo</th>
                  <th className="px-6 py-3">Score</th>
                  <th className="px-6 py-3">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {loading && reports.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                      <svg className="animate-spin w-6 h-6 mx-auto mb-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Carregando...
                    </td>
                  </tr>
                ) : reports.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      Nenhum relatorio encontrado. Gere sua primeira analise acima.
                    </td>
                  </tr>
                ) : (
                  reports.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-3 text-sm text-gray-300">{formatDate(r.created_at)}</td>
                      <td className="px-6 py-3">
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 font-medium capitalize">
                          {r.tipo || '---'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-400">{r.periodo_dias ? `${r.periodo_dias}d` : '---'}</td>
                      <td className="px-6 py-3">
                        <span className={`text-sm font-bold ${getScoreColor(r.score_saude)}`}>
                          {r.score_saude != null ? Number(r.score_saude) : '---'}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        <button
                          onClick={() => handleViewReport(r.id)}
                          className="text-purple-400 hover:text-purple-300 text-sm font-medium flex items-center gap-1"
                        >
                          <EyeIcon className="w-4 h-4" />
                          Ver
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {reportsPagination.pages > 1 && (
            <div className="px-6 py-3 border-t border-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-400">
                Pagina {reportsPage} de {reportsPagination.pages} ({reportsPagination.total} total)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setReportsPage((p) => Math.max(1, p - 1))}
                  disabled={reportsPage <= 1}
                  className="px-3 py-1 text-sm bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setReportsPage((p) => Math.min(reportsPagination.pages, p + 1))}
                  disabled={reportsPage >= reportsPagination.pages}
                  className="px-3 py-1 text-sm bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Proximo
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ============================================================
  // RENDER - Tab: Knowledge Base
  // ============================================================

  const renderKnowledgeBase = () => {
    // Group articles by category
    const grouped = {}
    kbArticles.forEach((article) => {
      const cat = article.categoria || 'geral'
      if (!grouped[cat]) grouped[cat] = []
      grouped[cat].push(article)
    })

    return (
      <div className="space-y-6">
        {/* Controls */}
        <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <div className="flex flex-wrap gap-3 flex-1">
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={kbSearch}
                  onChange={(e) => setKbSearch(e.target.value)}
                  placeholder="Buscar artigos..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg text-sm placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <select
                value={kbFilterCategoria}
                onChange={(e) => setKbFilterCategoria(e.target.value)}
                className="bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Todas categorias</option>
                {KB_CATEGORIAS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => {
                setEditingKb(null)
                setKbForm({ titulo: '', conteudo: '', categoria: 'geral', tags: '' })
                setShowKbModal(true)
              }}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center gap-2 text-sm shadow-lg shadow-purple-600/25"
            >
              <PlusIcon className="w-4 h-4" />
              Novo Artigo
            </button>
          </div>
        </div>

        {/* Articles grouped by category */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin w-8 h-8 text-purple-400" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
            <BookOpenIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">Nenhum artigo encontrado.</p>
            <p className="text-gray-500 text-sm mt-1">Crie o primeiro artigo da sua base de conhecimento.</p>
          </div>
        ) : (
          Object.entries(grouped).map(([cat, articles]) => {
            const catConfig = KB_CATEGORIAS.find((c) => c.value === cat) || { label: cat, color: 'bg-gray-500/20 text-gray-400' }
            return (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${catConfig.color}`}>
                    {catConfig.label}
                  </span>
                  <span className="text-xs text-gray-500">{articles.length} artigo{articles.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {articles.map((article) => (
                    <div
                      key={article.id}
                      className="bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-700 hover:border-purple-500/50 transition-all group"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-white font-medium text-sm leading-snug flex-1 mr-2">
                          {article.titulo}
                        </h4>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => openEditKb(article)}
                            className="p-1 text-gray-400 hover:text-blue-400 transition-colors"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteKb(article.id)}
                            className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-400 text-xs line-clamp-3 mb-3 leading-relaxed">
                        {article.conteudo}
                      </p>
                      {article.tags && (
                        <div className="flex flex-wrap gap-1">
                          {(Array.isArray(article.tags) ? article.tags : [article.tags]).map((tag, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-400">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 text-xs text-gray-500">
                        {formatDate(article.updated_at || article.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })
        )}

        {/* KB Modal */}
        {showKbModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-700">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
                <h3 className="text-lg font-semibold text-white">
                  {editingKb ? 'Editar Artigo' : 'Novo Artigo'}
                </h3>
                <button onClick={() => { setShowKbModal(false); setEditingKb(null) }} className="text-gray-400 hover:text-white">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Titulo *</label>
                  <input
                    type="text"
                    value={kbForm.titulo}
                    onChange={(e) => setKbForm({ ...kbForm, titulo: e.target.value })}
                    className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Titulo do artigo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Categoria</label>
                  <select
                    value={kbForm.categoria}
                    onChange={(e) => setKbForm({ ...kbForm, categoria: e.target.value })}
                    className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                  >
                    {KB_CATEGORIAS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Conteudo *</label>
                  <textarea
                    value={kbForm.conteudo}
                    onChange={(e) => setKbForm({ ...kbForm, conteudo: e.target.value })}
                    rows={6}
                    className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    placeholder="Conteudo do artigo..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Tags (separadas por virgula)</label>
                  <input
                    type="text"
                    value={kbForm.tags}
                    onChange={(e) => setKbForm({ ...kbForm, tags: e.target.value })}
                    className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="ex: locacao, frota, manutencao"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700">
                <button
                  onClick={() => { setShowKbModal(false); setEditingKb(null) }}
                  className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveKb}
                  className="px-4 py-2 text-sm text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-medium"
                >
                  {editingKb ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ============================================================
  // RENDER - Tab: Scheduler
  // ============================================================

  const renderScheduler = () => {
    const isRunning = schedulerStatus?.running || schedulerStatus?.status === 'running'
    const jobs = schedulerStatus?.jobs || schedulerStatus?.cron_jobs || []

    return (
      <div className="space-y-6">
        {/* Scheduler Status */}
        <div className="bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                isRunning ? 'bg-green-500/20' : 'bg-red-500/20'
              }`}>
                {isRunning ? (
                  <PlayIcon className="w-7 h-7 text-green-400" />
                ) : (
                  <StopIcon className="w-7 h-7 text-red-400" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Scheduler de Tarefas</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${
                    isRunning ? 'text-green-400' : 'text-red-400'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${isRunning ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                    {isRunning ? 'Em execucao' : 'Parado'}
                  </span>
                  {schedulerStatus?.uptime && (
                    <span className="text-xs text-gray-500">| Uptime: {schedulerStatus.uptime}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleStartScheduler}
                disabled={isRunning}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlayIcon className="w-4 h-4" />
                Iniciar
              </button>
              <button
                onClick={handleStopScheduler}
                disabled={!isRunning}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <StopIcon className="w-4 h-4" />
                Parar
              </button>
              <button
                onClick={() => { loadSchedulerStatus(); loadSchedulerLogs() }}
                className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                title="Atualizar"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Cron Jobs Table */}
        {jobs.length > 0 && (
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700">
            <div className="px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <CogIcon className="w-5 h-5 text-gray-400" />
                Jobs Configurados
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-gray-700">
                    <th className="px-6 py-3">Nome</th>
                    <th className="px-6 py-3">Schedule</th>
                    <th className="px-6 py-3">Descricao</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Ultima Execucao</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {jobs.map((job, idx) => (
                    <tr key={idx} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-3 text-sm text-white font-medium">{job.name || job.job_name || '---'}</td>
                      <td className="px-6 py-3">
                        <code className="text-xs bg-gray-900 text-purple-400 px-2 py-1 rounded">{job.schedule || job.cron || '---'}</code>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-400">{job.description || job.descricao || '---'}</td>
                      <td className="px-6 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          job.enabled !== false ? 'bg-green-500/20 text-green-400' : 'bg-gray-600/20 text-gray-400'
                        }`}>
                          {job.enabled !== false ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-xs text-gray-500">{formatDate(job.last_run || job.ultima_execucao)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Logs */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700">
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                Logs de Execucao
              </h3>
              <div className="flex gap-2">
                <select
                  value={logFilterJob}
                  onChange={(e) => setLogFilterJob(e.target.value)}
                  className="bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Todos jobs</option>
                  {jobs.map((job, idx) => (
                    <option key={idx} value={job.name || job.job_name}>
                      {job.name || job.job_name}
                    </option>
                  ))}
                </select>
                <select
                  value={logFilterStatus}
                  onChange={(e) => setLogFilterStatus(e.target.value)}
                  className="bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Todos status</option>
                  <option value="success">Sucesso</option>
                  <option value="error">Erro</option>
                  <option value="running">Executando</option>
                </select>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-800">
                <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-gray-700">
                  <th className="px-6 py-3">Data</th>
                  <th className="px-6 py-3">Job</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Duracao</th>
                  <th className="px-6 py-3">Detalhes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {schedulerLogs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      Nenhum log encontrado.
                    </td>
                  </tr>
                ) : (
                  schedulerLogs.map((log, idx) => (
                    <tr key={idx} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-3 text-xs text-gray-400">{formatDate(log.created_at || log.executed_at)}</td>
                      <td className="px-6 py-3 text-sm text-white font-medium">{log.job_name || '---'}</td>
                      <td className="px-6 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          log.status === 'success' ? 'bg-green-500/20 text-green-400' :
                          log.status === 'error' ? 'bg-red-500/20 text-red-400' :
                          log.status === 'running' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-600/20 text-gray-400'
                        }`}>
                          {log.status === 'success' ? 'Sucesso' :
                           log.status === 'error' ? 'Erro' :
                           log.status === 'running' ? 'Executando' :
                           log.status || '---'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-xs text-gray-400">{log.duration || log.duracao || '---'}</td>
                      <td className="px-6 py-3 text-xs text-gray-500 max-w-xs truncate">{log.details || log.detalhes || log.message || '---'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 border-t border-gray-700 text-xs text-gray-500 flex items-center gap-2">
            <InformationCircleIcon className="w-4 h-4" />
            Atualizado automaticamente a cada 30 segundos
          </div>
        </div>
      </div>
    )
  }

  // ============================================================
  // RENDER - Tab: Alertas
  // ============================================================

  const renderAlertas = () => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Alertas Configurados</h3>
            <p className="text-sm text-gray-400 mt-1">Gerencie notificacoes automaticas baseadas em condicoes</p>
          </div>
          <button
            onClick={() => {
              setEditingAlerta(null)
              setAlertaForm({ nome: '', tipo: 'lead_score', condicao: '', canal_notificacao: 'email', destinatarios: '', ativo: true })
              setShowAlertaModal(true)
            }}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center gap-2 text-sm shadow-lg shadow-purple-600/25"
          >
            <PlusIcon className="w-4 h-4" />
            Novo Alerta
          </button>
        </div>

        {/* Alerts list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin w-8 h-8 text-purple-400" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : alertas.length === 0 ? (
          <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
            <BellAlertIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">Nenhum alerta configurado.</p>
            <p className="text-gray-500 text-sm mt-1">Crie alertas para receber notificacoes automaticas.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {alertas.map((alerta) => (
              <div
                key={alerta.id}
                className={`bg-gray-800 rounded-xl p-5 shadow-lg border transition-all ${
                  alerta.ativo ? 'border-gray-700 hover:border-purple-500/50' : 'border-gray-700/50 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <BellAlertIcon className={`w-5 h-5 ${alerta.ativo ? 'text-purple-400' : 'text-gray-500'}`} />
                      <h4 className="text-white font-medium">{alerta.nome}</h4>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-400 capitalize">
                        {alerta.tipo}
                      </span>
                      <span className="text-xs text-gray-500">
                        via {alerta.canal_notificacao || 'email'}
                      </span>
                    </div>
                  </div>
                  {/* Toggle */}
                  <button
                    onClick={() => handleToggleAlerta(alerta)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      alerta.ativo ? 'bg-purple-600' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                        alerta.ativo ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {alerta.condicao && (
                  <p className="text-sm text-gray-400 mb-3 bg-gray-900/50 rounded-lg px-3 py-2">
                    {alerta.condicao}
                  </p>
                )}

                {alerta.destinatarios && (
                  <p className="text-xs text-gray-500 mb-2">
                    Destinatarios: {alerta.destinatarios}
                  </p>
                )}

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-700">
                  <span className="text-xs text-gray-500">
                    Ultimo disparo: {alerta.ultimo_disparo ? formatDate(alerta.ultimo_disparo) : 'Nunca'}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEditAlerta(alerta)}
                      className="p-1.5 text-gray-400 hover:text-blue-400 transition-colors rounded-lg hover:bg-gray-700"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteAlerta(alerta.id)}
                      className="p-1.5 text-gray-400 hover:text-red-400 transition-colors rounded-lg hover:bg-gray-700"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Alerta Modal */}
        {showAlertaModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-700">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
                <h3 className="text-lg font-semibold text-white">
                  {editingAlerta ? 'Editar Alerta' : 'Novo Alerta'}
                </h3>
                <button onClick={() => { setShowAlertaModal(false); setEditingAlerta(null) }} className="text-gray-400 hover:text-white">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Nome *</label>
                  <input
                    type="text"
                    value={alertaForm.nome}
                    onChange={(e) => setAlertaForm({ ...alertaForm, nome: e.target.value })}
                    className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Nome do alerta"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Tipo</label>
                    <select
                      value={alertaForm.tipo}
                      onChange={(e) => setAlertaForm({ ...alertaForm, tipo: e.target.value })}
                      className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="lead_score">Lead Score</option>
                      <option value="conversao">Conversao</option>
                      <option value="campanha">Campanha</option>
                      <option value="financeiro">Financeiro</option>
                      <option value="contrato">Contrato</option>
                      <option value="veiculo">Veiculo</option>
                      <option value="custom">Personalizado</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Canal</label>
                    <select
                      value={alertaForm.canal_notificacao}
                      onChange={(e) => setAlertaForm({ ...alertaForm, canal_notificacao: e.target.value })}
                      className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="email">Email</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="sistema">Sistema</option>
                      <option value="todos">Todos</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Condicao</label>
                  <textarea
                    value={alertaForm.condicao}
                    onChange={(e) => setAlertaForm({ ...alertaForm, condicao: e.target.value })}
                    rows={3}
                    className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    placeholder="Descreva a condicao para disparo do alerta..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Destinatarios</label>
                  <input
                    type="text"
                    value={alertaForm.destinatarios}
                    onChange={(e) => setAlertaForm({ ...alertaForm, destinatarios: e.target.value })}
                    className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Emails separados por virgula"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-300">Ativo</label>
                  <button
                    type="button"
                    onClick={() => setAlertaForm({ ...alertaForm, ativo: !alertaForm.ativo })}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      alertaForm.ativo ? 'bg-purple-600' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                        alertaForm.ativo ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700">
                <button
                  onClick={() => { setShowAlertaModal(false); setEditingAlerta(null) }}
                  className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveAlerta}
                  className="px-4 py-2 text-sm text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-medium"
                >
                  {editingAlerta ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ============================================================
  // RENDER - Tab: Metricas
  // ============================================================

  const renderMetricas = () => {
    // Compute aggregate metrics
    const totalLeads = metricasDiarias.reduce((sum, d) => sum + (Number(d.novos_leads) || 0), 0)
    const totalConversoes = metricasDiarias.reduce((sum, d) => sum + (Number(d.conversoes) || 0), 0)
    const totalCampanhas = metricasDiarias.reduce((sum, d) => sum + (Number(d.campanhas_enviadas) || 0), 0)
    const avgScore = metricasDiarias.length > 0
      ? (metricasDiarias.reduce((sum, d) => sum + (Number(d.score_medio) || 0), 0) / metricasDiarias.length).toFixed(1)
      : 0
    const taxaConversao = totalLeads > 0 ? ((totalConversoes / totalLeads) * 100).toFixed(1) : 0

    // Find max values for bar normalization
    const maxLeads = Math.max(...metricasDiarias.map((d) => Number(d.novos_leads) || 0), 1)
    const maxConversoes = Math.max(...metricasDiarias.map((d) => Number(d.conversoes) || 0), 1)
    const maxCampanhas = Math.max(...metricasDiarias.map((d) => Number(d.campanhas_enviadas) || 0), 1)

    return (
      <div className="space-y-6">
        {/* Period Selector */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-purple-400" />
            Metricas Diarias
          </h3>
          <div className="flex gap-1">
            {METRIC_PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setMetricasPeriodo(p.value)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-all ${
                  metricasPeriodo === p.value
                    ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <BoltIcon className="w-5 h-5 text-blue-400" />
              </div>
              <span className="text-sm text-gray-400">Novos Leads</span>
            </div>
            <p className="text-2xl font-bold text-white">{totalLeads}</p>
          </div>
          <div className="bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircleIcon className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-sm text-gray-400">Conversoes</span>
            </div>
            <p className="text-2xl font-bold text-white">{totalConversoes}</p>
          </div>
          <div className="bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <RocketLaunchIcon className="w-5 h-5 text-purple-400" />
              </div>
              <span className="text-sm text-gray-400">Campanhas</span>
            </div>
            <p className="text-2xl font-bold text-white">{totalCampanhas}</p>
          </div>
          <div className="bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <ArrowTrendingUpIcon className="w-5 h-5 text-amber-400" />
              </div>
              <span className="text-sm text-gray-400">Taxa Conversao</span>
            </div>
            <p className="text-2xl font-bold text-white">{taxaConversao}%</p>
          </div>
          <div className="bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                <ShieldCheckIcon className="w-5 h-5 text-pink-400" />
              </div>
              <span className="text-sm text-gray-400">Score Medio</span>
            </div>
            <p className={`text-2xl font-bold ${getScoreColor(avgScore)}`}>{avgScore}</p>
          </div>
        </div>

        {/* Charts */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin w-8 h-8 text-purple-400" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : metricasDiarias.length === 0 ? (
          <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
            <ChartBarIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">Sem dados de metricas para o periodo selecionado.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Leads Chart */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-5">
              <h4 className="text-sm font-semibold text-gray-300 mb-4">Novos Leads / Dia</h4>
              <div className="flex items-end gap-[2px] h-40">
                {metricasDiarias.map((d, idx) => {
                  const val = Number(d.novos_leads) || 0
                  const pct = (val / maxLeads) * 100
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center group relative">
                      <div
                        className="w-full bg-blue-500 rounded-t-sm transition-all hover:bg-blue-400 min-h-[2px]"
                        style={{ height: `${Math.max(pct, 2)}%` }}
                      />
                      {/* Tooltip */}
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10 shadow-lg">
                        {formatDateShort(d.data)}: {val}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>{formatDateShort(metricasDiarias[0]?.data)}</span>
                <span>{formatDateShort(metricasDiarias[metricasDiarias.length - 1]?.data)}</span>
              </div>
            </div>

            {/* Conversoes Chart */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-5">
              <h4 className="text-sm font-semibold text-gray-300 mb-4">Conversoes / Dia</h4>
              <div className="flex items-end gap-[2px] h-40">
                {metricasDiarias.map((d, idx) => {
                  const val = Number(d.conversoes) || 0
                  const pct = (val / maxConversoes) * 100
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center group relative">
                      <div
                        className="w-full bg-green-500 rounded-t-sm transition-all hover:bg-green-400 min-h-[2px]"
                        style={{ height: `${Math.max(pct, 2)}%` }}
                      />
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10 shadow-lg">
                        {formatDateShort(d.data)}: {val}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>{formatDateShort(metricasDiarias[0]?.data)}</span>
                <span>{formatDateShort(metricasDiarias[metricasDiarias.length - 1]?.data)}</span>
              </div>
            </div>

            {/* Campanhas Chart */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-5">
              <h4 className="text-sm font-semibold text-gray-300 mb-4">Campanhas Enviadas / Dia</h4>
              <div className="flex items-end gap-[2px] h-40">
                {metricasDiarias.map((d, idx) => {
                  const val = Number(d.campanhas_enviadas) || 0
                  const pct = (val / maxCampanhas) * 100
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center group relative">
                      <div
                        className="w-full bg-purple-500 rounded-t-sm transition-all hover:bg-purple-400 min-h-[2px]"
                        style={{ height: `${Math.max(pct, 2)}%` }}
                      />
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10 shadow-lg">
                        {formatDateShort(d.data)}: {val}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>{formatDateShort(metricasDiarias[0]?.data)}</span>
                <span>{formatDateShort(metricasDiarias[metricasDiarias.length - 1]?.data)}</span>
              </div>
            </div>

            {/* Score History Chart */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-5">
              <h4 className="text-sm font-semibold text-gray-300 mb-4">Score Medio / Dia</h4>
              <div className="flex items-end gap-[2px] h-40">
                {metricasDiarias.map((d, idx) => {
                  const val = Number(d.score_medio) || 0
                  const pct = val // Score is 0-100 already
                  const barColor = val >= 80 ? 'bg-green-500 hover:bg-green-400' :
                                   val >= 60 ? 'bg-blue-500 hover:bg-blue-400' :
                                   val >= 40 ? 'bg-yellow-500 hover:bg-yellow-400' :
                                   'bg-red-500 hover:bg-red-400'
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center group relative">
                      <div
                        className={`w-full rounded-t-sm transition-all min-h-[2px] ${barColor}`}
                        style={{ height: `${Math.max(pct, 2)}%` }}
                      />
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10 shadow-lg">
                        {formatDateShort(d.data)}: {val}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>{formatDateShort(metricasDiarias[0]?.data)}</span>
                <span>{formatDateShort(metricasDiarias[metricasDiarias.length - 1]?.data)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        {metricasDiarias.length > 0 && (
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700">
            <div className="px-6 py-4 border-b border-gray-700">
              <h4 className="text-sm font-semibold text-gray-300">Detalhamento Diario</h4>
            </div>
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-gray-800">
                  <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-gray-700">
                    <th className="px-6 py-3">Data</th>
                    <th className="px-6 py-3">Leads</th>
                    <th className="px-6 py-3">Conversoes</th>
                    <th className="px-6 py-3">Campanhas</th>
                    <th className="px-6 py-3">Score</th>
                    <th className="px-6 py-3">Receita</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {[...metricasDiarias].reverse().map((d, idx) => (
                    <tr key={idx} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-2.5 text-sm text-gray-300">{formatDateShort(d.data)}</td>
                      <td className="px-6 py-2.5 text-sm text-blue-400 font-medium">{Number(d.novos_leads) || 0}</td>
                      <td className="px-6 py-2.5 text-sm text-green-400 font-medium">{Number(d.conversoes) || 0}</td>
                      <td className="px-6 py-2.5 text-sm text-purple-400 font-medium">{Number(d.campanhas_enviadas) || 0}</td>
                      <td className="px-6 py-2.5">
                        <span className={`text-sm font-bold ${getScoreColor(d.score_medio)}`}>
                          {Number(d.score_medio) || 0}
                        </span>
                      </td>
                      <td className="px-6 py-2.5 text-sm text-gray-300">
                        {d.receita != null ? `R$ ${Number(d.receita).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '---'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ============================================================
  // MAIN RENDER
  // ============================================================

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-600/25">
            <SparklesIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">IA Estrategista</h1>
            <p className="text-sm text-gray-400">Inteligencia artificial para analise e otimizacao de marketing</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-700">
        <nav className="flex gap-1 overflow-x-auto pb-px -mb-px">
          {TABS.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                  isActive
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'estrategista' && renderEstrategista()}
        {activeTab === 'knowledge' && renderKnowledgeBase()}
        {activeTab === 'scheduler' && renderScheduler()}
        {activeTab === 'alertas' && renderAlertas()}
        {activeTab === 'metricas' && renderMetricas()}
      </div>
    </div>
  )
}

export default MarketingAIPage
